import "./index.css";
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useLocation,
} from "react-router-dom";
import { Redirect } from "react-router-dom/cjs/react-router-dom.min";
import { CartProvider } from "hooks/useCart";
import { useContactId } from "hooks/useContactId";
import LoadingIndicator from "components/LoadingIndicator";
import ErrorModal from "components/ErrorModal";
import FreeTrialGuard from "components/FreeTrialGuard";
import env from "./environment";
import { makeAuthedRequest } from "utils/makeAuthedRequest";
import { trackPageView, identifyUser } from "utils/analytics";

import CartAddressPage from "pages/CartAddressPage";
import CartPaymentPage from "pages/CartPaymentPage";
import WWGStorePage from "pages/WWGStore";
import StockItemDetailsPage from "pages/StockItemDetailsPage";
import MyListPage from "pages/MyListPage";
import MyOrders from "pages/MyOrders";
import OrderDetails from "./pages/OrderDetails/OrderDetails";
import CartPage from "pages/CartPage";

// Guards against a redirect loop if Core API keeps telling us to redirect
// for verification. Unlike the login redirect, failing to verify isn't
// fatal to viewing the app, so once the cap is hit we just stop asking.
const VERIFICATION_REDIRECT_COUNT_KEY = "wwg-verification-redirect-count";
const MAX_VERIFICATION_REDIRECTS = 3;

function App() {
  const location = useLocation();
  const contactId = useContactId();

  // Segment's snippet only fires page() once, on initial load - fire it
  // again on every client-side route change so SPA navigation shows up as
  // distinct pageviews.
  useEffect(() => {
    trackPageView();
  }, [location.pathname, location.search]);

  // Ties analytics activity to the resolved contact once we know who they
  // are. Safe to re-run if contactId changes (e.g. ?contactId= override).
  useEffect(() => {
    identifyUser(contactId);
  }, [contactId]);

  // Ask Core API whether this user needs to be redirected elsewhere to
  // complete verification. Capped to avoid looping if Core API keeps
  // sending us back here.
  useEffect(() => {
    const redirectCount = Number(
      sessionStorage.getItem(VERIFICATION_REDIRECT_COUNT_KEY) || "0"
    );

    if (redirectCount >= MAX_VERIFICATION_REDIRECTS) {
      return;
    }

    makeAuthedRequest(
      `${env.coreApiBase}/verification/redirect`,
      {
        method: "post",
        body: JSON.stringify({ currentAppUrl: window.location.href }),
      },
      env.coreApiBase
    )
      .then(async (res) => {
        const json = await res.json();

        if (res.ok && json.shouldRedirect && json.redirectUrl) {
          sessionStorage.setItem(
            VERIFICATION_REDIRECT_COUNT_KEY,
            String(redirectCount + 1)
          );

          window.location.assign(json.redirectUrl);
        } else {
          sessionStorage.removeItem(VERIFICATION_REDIRECT_COUNT_KEY);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CartProvider>
      <LoadingIndicator />
      <ErrorModal />
      <Router>
        <Switch>
          <Route exact path="/my-orders">
            <FreeTrialGuard>
              <MyOrders />
            </FreeTrialGuard>
          </Route>
          <Route exact path="/order/:orderId">
            <FreeTrialGuard>
              <OrderDetails />
            </FreeTrialGuard>
          </Route>
          <Route exact path="/wwg-store">
            <WWGStorePage />
          </Route>
          <Route path="/my-lists">
            <MyListPage />
          </Route>
          <Route exact path="/cart">
            <FreeTrialGuard>
              <CartPage />
            </FreeTrialGuard>
          </Route>
          <Route path="/cart/address">
            <FreeTrialGuard>
              <CartAddressPage />
            </FreeTrialGuard>
          </Route>
          <Route path="/cart/payment">
            <FreeTrialGuard>
              <CartPaymentPage />
            </FreeTrialGuard>
          </Route>
          <Route
            exact
            path="/stock-details/:stockId"
            render={(props) => (
              <StockItemDetailsPage
                {...props}
                pageFrom={props.location.state?.pageFrom}
              />
            )}
          />
          <Route path="*">
            <Redirect exact from="/" to="/wwg-store" />
          </Route>
        </Switch>
      </Router>
    </CartProvider>
  );
}

export default App;
